import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { toHttpParams } from '../http/http-params';
import { MAX_PAGE_SIZE, PagedResult } from '../models/common.models';
import {
  RejectionTarget,
  TaskActionResult,
  TaskQuery,
  WorkflowAuditEntry,
  WorkflowInstanceDetail,
  WorkflowTask,
} from '../models/workflow.models';

/** Approvals (/api/workflow): my pending tasks, deciding them, and a request's workflow and timeline. */
@Injectable({ providedIn: 'root' })
export class WorkflowService {
  private readonly http = inject(HttpClient);
  private readonly url = `${environment.apiBaseUrl}/workflow`;

  myTasks(query: TaskQuery = {}): Observable<PagedResult<WorkflowTask>> {
    return this.http.get<PagedResult<WorkflowTask>>(`${this.url}/tasks/mine`, {
      params: toHttpParams({ pageNumber: 1, pageSize: MAX_PAGE_SIZE, ...query }),
    });
  }

  approve(taskId: string, comments?: string): Observable<TaskActionResult> {
    return this.http.post<TaskActionResult>(`${this.url}/tasks/${taskId}/approve`, { comments: comments || null });
  }

  /** Returns the request: to the requester (who edits and resubmits) or to an earlier level, which decides again. */
  rejectTo(taskId: string, comments: string, levelOrder: number | null): Observable<TaskActionResult> {
    const target: RejectionTarget = levelOrder === null ? 'Initiator' : 'Level';
    return this.http.post<TaskActionResult>(`${this.url}/tasks/${taskId}/reject`, { comments, target, targetLevelOrder: levelOrder });
  }

  /** Puts the request on hold (the last level only); a reason is required. */
  hold(taskId: string, comments: string): Observable<TaskActionResult> {
    return this.http.post<TaskActionResult>(`${this.url}/tasks/${taskId}/hold`, { comments });
  }

  /** Takes the request off hold; it is in approval again at the same level. */
  resume(taskId: string, comments?: string): Observable<TaskActionResult> {
    return this.http.post<TaskActionResult>(`${this.url}/tasks/${taskId}/resume`, { comments: comments || null });
  }

  instance(instanceId: string): Observable<WorkflowInstanceDetail> {
    return this.http.get<WorkflowInstanceDetail>(`${this.url}/instances/${instanceId}`);
  }

  timeline(instanceId: string): Observable<WorkflowAuditEntry[]> {
    return this.http.get<WorkflowAuditEntry[]>(`${this.url}/instances/${instanceId}/timeline`);
  }
}
